import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router";
import BreadCrumbs from "../../../components/common/BreadCrumbs/BreadCrumbs";
import CardTable from "../../../components/common/CardTable";
import Pagination from "../../../components/common/Table/Pagination";
import Table, { TableRow } from "../../../components/common/Table/Table";
import { projectTabs } from "../../../config/tabsConfig";
import { useProfile } from "../../../contexts/ProfileContext";
import Loader from "../../../components/common/Loader";
import Notifications from "../../../components/common/Notifications";
import Tabs from "../../../components/common/Tabs";
import { useAppSelector, useAppDispatch } from "../../../store/hooks";
import {
  fetchTeamMembers
} from "../../../store/slices/memberSlice";
import {
  fetchPipList,
  setProjectDetailCurrentPage,
  resetPipListState,
} from "../../../store/slices/pipListSlice";
import {
  fetchProjectDetails,
  fetchProjectAccess,
  fetchTeamTrackedStatus,
  addTeamToMyTeams,
  setSelectedProjectIndex,
  deleteProject,
  deleteMyTeam,
  teamsInfoColumn,
  projectListColumn,
 resetProjectDetails } from "../../../store/slices/projectDetailsSlice";
import { teamStorage } from "../../../utils/teamStorage";
import {
  fetchTestsOfChange,
  resetTestsOfChangeState,
  setTestsOfChangeCurrentPage,
  addTestOfChange,
  deleteTestOfChange,
  updateTestOfChange,
} from "../../../store/slices/testsOfChangeSlice";
import {
  fetchRatingAndSource,
  selectRatings,
  selectIdeaSources,
  //selectRatingSourceLoading,
} from "../../../store/slices/ratingSourceSlice";
import {
  fetchFocusAreaValueCompassList,
  fetchFocusAreaList,
  fetchMetricList,
  fetchDatasourceList,
  createProject,
  updateProject,
} from "../../../store/slices/ManageProjectSlice";
import { fetchShiftLookup, selectShiftOptions } from "../../../store/slices/shiftLookupSlice";
import {
  fetchTestDataSeries,
  setTestDataSeriesCurrentPage,
  addTestDataSeries,
  deleteTestDataSeries,
  updateTestDataSeries
, resetTestDataSeriesState } from "../../../store/slices/testDataSeriesSlice";
import { Link } from "react-router-dom";
import CommonErrorBanner from "../../../components/common/CommonErrorBanner";
import DeleteModal from "./ProjectHelper/DeleteModal";
import DeleteBanner from "./ProjectHelper/DeleteBanner";
import useDeleteModal from "./ProjectHelper/useDeleteModal";
import NoProjectBanner from "./ProjectHelper/NoProjectBanner";
import {
  ProjectFailureBanner,
  useProjectBanner,
} from "./ProjectHelper/ProjectStatusBanner";
import { formatTimestampMDY } from "../../../utils/date";
import { normalizeShiftToDropdown , normalizeRatingToDropdown, normalizeIdeaToDropdown } from "../../../utils/normalizeDropdown";
import { useUnsavedChangesPrompt } from "../../../hooks/PathToPerformance/useUnsavedChangesPrompt";
import Modal from "../../../components/common/Modal";
import Dropdown, { DropdownOption } from "../../../components/common/Dropdown";
import {
  exportTestOfChangeCSV,
  selectReportLoading,
  selectReportError,
  clearReportState,
  downloadProjectRecordPDF,
  downloadTestOfChangePDF,
  downloadPipDetailsReport,
  selectProjectPdfLoading,
  selectTocPdfLoading,
} from "../../../store/slices/reportSlice";
import {
  getDetailsContainerTeamReportOptions,
  getDetailsContainerProjectReportOptions,
  EXPORT_TOC_PIP_OPTION_ID,
  EXPORT_TOC_PIP_OPTION_VALUE,
  EXPORT_PIP_HISTORY_OPTION_ID,
  EXPORT_PIP_DETAILS_OPTION_ID,
  PRINT_PROJECT_RECORDS_OPTION_ID,
  PRINT_TEST_OF_CHANGE_OPTION_ID,
  EXPORT_PROJECT_TEST_DATA_OPTION_ID,
  TEST_DATA_CHARTS_OPTION_ID,
  TOC_NO_DATA_BANNER_MESSAGE,
} from "../../../constants/reportDropdownOptions";

import { downloadPipHistoryReport, selectReportDownloadLoading, downloadTestSeriesByCriteriaReport } from "../../../store/slices/teamReportSlice"; 
import { downloadDisplaySummaryAndDetailsReport, isPdfDownloadLoading } from "../../../store/slices/teamsReportSlice";
import {
  TEAM_SUMMARY_OPTION_ID,
  TEAM_DETAILS_OPTION_ID,
} from "../../../utils/reportConstants";
import api from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import { buildHtmlString, ChartData } from "../../../utility/testDataCharts";

/**
 * Formats boolean field values for display.
 * Converts "Yes"/"No" to "Y"/"N" format.
 */
const formatBooleanField = (value: string | undefined): string => {
  if (value === 'Yes') return 'Y';
  if (value === 'No') return 'N';
  return value ?? '';
};

/**
 * Determines if a project is currently active/viewing based on index and ID matching.
 */
const isProjectActive = (
  rowIndex: number,
  perfId: number,
  selectedProjectIndex: number | null,
  viewingIndex: number | null,
  selectedProject: any,
): boolean => {
  const isActiveByIndex = (selectedProjectIndex ?? viewingIndex) === rowIndex;
  const isActiveById = selectedProject?.perfId != null && selectedProject?.perfId === perfId;
  return isActiveByIndex || isActiveById;
};

/**
 * Normalizes project access data from various payload shapes.
 */
const normalizeProjectAccessData = (projectAccess: any): any => {
  return (
    projectAccess?.data ??
    projectAccess?.access ??
    projectAccess
  );
};

/**
 * Determines if delete actions should be shown based on user permissions.
 */
const canShowDeleteActionsForProject = (normalizedProjectAccess: any, isAdmin: boolean | undefined): boolean => {
  return Boolean(
    (normalizedProjectAccess?.canEdit ?? isAdmin) &&
    (normalizedProjectAccess?.canDelete ?? isAdmin)
  );
};

/**
 * Resolves the target project to auto-select when the project list first loads.
 * If a pipId is provided, finds the project whose perfId matches it.
 * Falls back to the first project in the list when no match is found.
 *
 * @param projectList - The list of loaded projects.
 * @param selectedPipId - The pipId passed via navigation state or sessionStorage.
 * @param startIndex - The pagination start index offset.
 * @returns An object with the resolved `project` and its `listIndex` within the array.
 */
const resolvePipIdTargetProject = (
  projectList: any[],
  selectedPipId: string | null,
  startIndex: number,
): { project: any; listIndex: number } => {
  if (selectedPipId) {
    const pipIdNum = Number(selectedPipId);
    const matchIndex = projectList.findIndex((p: any) => Number(p.perfId) === pipIdNum);
    if (matchIndex !== -1) {
      return { project: projectList[matchIndex], listIndex: matchIndex };
    }
  }
  // Fall back to the first project accounting for pagination offset
  const fallbackIndex = projectList[startIndex] ? startIndex : 0;
  return { project: projectList[fallbackIndex], listIndex: fallbackIndex };
};

/**
 * Notification component displayed when no UBT team is selected.
 * Provides breadcrumbs and a link to the Select UBT page.
 */
const NoTeamSelectedNotification: React.FC<{ onBreadcrumbClick: () => void }> = ({ onBreadcrumbClick }) => (
  <div className="min-h-[70vh] lg:mx-40! mx-5!">
    <div className="mb-6!">
      <BreadCrumbs
        items={[
          { label: 'UBT Home', path: '/home', onClick: onBreadcrumbClick },
          { label: 'Projects' },
        ]}
      />
    </div>
    <h1 className="h1 tablet:mt-8! mobile:mt-3.5!">Performance improvement projects</h1>
    <div className="mt-8! max-w-[80%]">
      <Notifications
        heading="Please select an UBT"
        variant="Alert"
        messageInfo="Please select an UBT before accessing Projects page. You can access the page from Select UBT/ My Teams etc."
      >
        <ul className="flex items-start list-disc m-0 my-0! ml-4 mt-5!">
          <li>
            <Link
              to="/home/select-ubt"
              data-analytics-type="link"
              className="text-kp-blue-dark underline text-left text-[16px]! w-full bg-transparent border-none p-0 cursor-pointer hover:text-kp-blue"
              aria-label="Navigate to Select a UBT page"
            >
              Select a UBT
            </Link>
          </li>
        </ul>
      </Notifications>
    </div>
  </div>
);

/**
 * Displays team details in a card table format.
 * Shows team information or a "No Record found" message if no data is available.
 */
const TeamDetailsCard: React.FC<{
  teamRowDetail: TableRow[];
  teamsColumns: any;
}> = ({ teamRowDetail, teamsColumns }) => (
  <CardTable>
    {Array.isArray(teamRowDetail) && teamRowDetail.length > 0 ? (
      <Table columns={teamsColumns} rows={teamRowDetail} />
    ) : (
      <p className="p3 -book text-center mt-2.5! desktop:mt-50!">
        No Record found.
      </p>
    )}
  </CardTable>
);

const ProjectDetails: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    projectList,
    projectListCurrentPage,
    projectListPerPage,
    projectListLoading,
    projectListTotalItems,
  } = useAppSelector(
    (state) => state.pipListSlice,
  );

  const {
    testOfChangeList,
    testOfChangeCurrentPage,
    testOfChangePerPage,
    testOfChangeLoading,
    testOfChangeError,
    testOfChangeTotalItems,
  } = useAppSelector(
    (state) => state.testsOfChangeSlice,
  );

  const {
    focusAreaList,
    metricList,
    datasourceList,
    manageProjectDependentDropdownLoading,
    createProjectLoading,
    updateProjectLoading,
  } = useAppSelector(
    (state) => state.manageProjectSlice,
  );

  // Ratings and idea sources fetched from the backend
  const ratings = useAppSelector(selectRatings);
  const ideaSources = useAppSelector(selectIdeaSources);

  
  const [testOfChangeSortColumn, setTestOfChangeSortColumn] = useState<string>("datelastupd");
  const [testOfChangeSortDirection, setTestOfChangeSortDirection] = useState<"asc" | "desc">("desc");

  // Test data series (lifted from TestDataTab)
  const {
    testDataSeriesItems,
    testDataSeriesCurrentPage,
    testDataSeriesPerPage,
    testDataSeriesLoading,
    testDataSeriesError,
    testDataSeriesTotalItems,
  } = useAppSelector((state) => state.testDataSeriesSlice);

  const [testDataSeriesSortColumn, setTestDataSeriesSortColumn] = useState<string>("testDate");
  const [testDataSeriesSortDirection, setTestDataSeriesSortDirection] = useState<"asc" | "desc">("desc");
  const [testDataSeriesUiSortColumn, setTestDataSeriesUiSortColumn] = useState<string>("testDate");
  const {
    bannerRef: successBannerRef,
    isVisible: showCreateSuccessBanner,
    message: createSuccessMessage,
    showBanner: triggerSuccessBanner,
  } = useProjectBanner("Project created successfully.");
  const {
    bannerRef: manageErrorBannerRef,
    isVisible: showManageErrorBanner,
    message: manageErrorBannerMessage,
    showBanner: triggerManageErrorBanner,
  } = useProjectBanner("Failed to save project.");
  const {
    bannerRef: errorBannerRef,
    isVisible: showErrorBanner,
    message: errorBannerMessage,
    placement: errorBannerPlacement,
    showBanner: triggerErrorBanner,
  } = useProjectBanner("An error occurred.");

  const showSuccessBanner = useCallback((message: string) => {
    try {
      triggerSuccessBanner(message);
    } catch (e) {
      console.warn('Failed to show success banner:', e);
    }
  }, [triggerSuccessBanner]);

  const displayErrorBanner = useCallback((message: string, placement: "summary" | "detail" = "detail") => {
    try {
      triggerErrorBanner(message, placement);
    } catch (e) {
      console.warn('Failed to show error banner:', e);
    }
  }, [triggerErrorBanner]);

  const displayManageProjectErrorBanner = useCallback((message: string) => {
    try {
      triggerManageErrorBanner(message);
    } catch (e) {
      console.warn('Failed to show manage project error banner:', e);
    }
  }, [triggerManageErrorBanner]);

  const handleTestDataSeriesListSort = useCallback(
    (columnKey: string, direction: "asc" | "desc") => {
      const apiColumnMap: { [key: string]: string } = {
        'testDate': 'testDate',
        'shift': 'shift',
        'notes': 'notes',
        'denominatorValue': 'denominatorValue',
        'numeratorValue': 'numeratorValue',
        'outsideVarValue': 'outsideVarValue',

      };
      const apiSortColumn = apiColumnMap[columnKey] ?? columnKey.toUpperCase();
      setTestDataSeriesUiSortColumn(columnKey);
      setTestDataSeriesSortColumn(apiSortColumn);
      setTestDataSeriesSortDirection(direction);
      dispatch(setTestDataSeriesCurrentPage(1));
    },
    [dispatch],
  );

  const handleTestDataSeriesPageChange = useCallback((page: number) => {
    dispatch(setTestDataSeriesCurrentPage(page));
  }, [dispatch]);



  // Handler to allow child tabs to change the current page for tests of change
  const handleSetTestsOfChangePage = useCallback((page: number) => {
    dispatch(setTestsOfChangeCurrentPage(page));
  }, [dispatch]);

  const handleSetTestsOfChangeSort = useCallback((columnKey: string, direction: "asc" | "desc") => {
    setTestOfChangeSortColumn(columnKey);
    setTestOfChangeSortDirection(direction);
  }, []);

  const {
    projectDetails,
    projectDetailsLoading,
    projectDetailsError,
    projectAccess,
    projectAccessLoading,
    isTeamTracked,
    isTeamTrackedLoading,
    addTeamToMyTeamsLoading,
  } = useAppSelector((s) => s.pipDetailsSlice);

  const selectedProjectIndex = useAppSelector((s) => s.pipDetailsSlice.selectedProjectIndex);

  const {
    teamDetails,
    loading: teamMembersLoading,
    teamMembersStatus,
  } = useAppSelector((state) => state.membersSlice);
  // Navigate to UBTLandingPage if teamMembersStatus is 404 (team not found)
  useEffect(() => {
    if (teamMembersStatus === 404) {
      navigate("/home");
    }
  }, [teamMembersStatus, navigate]);

  const [viewingIndex, setViewingIndex] = useState<number | null>(null);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedFocusId, setSelectedFocusId] = useState<string | null>(null);
  const [currentValueCompassId, setCurrentValueCompassId] = useState<string | null>(null);
  const [pendingMetricId, setPendingMetricId] = useState<string | number | null>(null);
  const [selectedTestOfChange, setSelectedTestOfChange] = useState<any>(null);
  const [activeTabId, setActiveTabId] = useState<number | undefined>(undefined);
  const [forceShowTabs, setForceShowTabs] = useState<boolean>(false);
  const [triggerAddFromBanner, setTriggerAddFromBanner] = useState<boolean>(false);
  const [pendingSelectFirstOnListUpdate, setPendingSelectFirstOnListUpdate] = useState<boolean>(false);
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const listRefreshStartedRef = useRef<boolean>(false);
  const [projectListSortColumn, setProjectListSortColumn] = useState<string>("status");
  const [projectListSortDirection, setProjectListSortDirection] = useState<"asc" | "desc">("asc");
  const [projectListUiSortColumn, setProjectListUiSortColumn] = useState<string>("status");
  const [currentProjectIdToDelete, setCurrentProjectIdToDelete] = useState<number | null>(null);
  const [currentTeamIdToDelete, setCurrentTeamIdToDelete] = useState<number | null>(null);
  const [deleteBannerPlacement, setDeleteBannerPlacement] = useState<"summary" | "detail">("detail");
  const [delayNoProjectBannerUntilDeleteBannerEnds, setDelayNoProjectBannerUntilDeleteBannerEnds] = useState<boolean>(false);
  const [isDeleteFlowLoading, setIsDeleteFlowLoading] = useState<boolean>(false);
  const isMountedRef = useRef(true);
  const deleteBannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    showDeleteModal,
    showTeamDeleteModel,
    setShowTeamDeleteModel,
    setShowDeleteModal,
    deletedResponseName,
    setDeletedResponseName,
    showDeleteBanner,
    setShowDeleteBanner,
    responseForms,
    setResponseForms,
  } = useDeleteModal();

  // Add to my teams banner state
  const [showAddTeamBanner, setShowAddTeamBanner] = useState<boolean>(false);
  const [addTeamBannerMessage, setAddTeamBannerMessage] = useState<string>("");
  const [addTeamBannerVariant, setAddTeamBannerVariant] = useState<"Error" | "Success">("Error");
  const addTeamBannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Test-of-change CSV export — loading / error state from reportSlice
  const tocExportLoading = useAppSelector(selectReportLoading);
  const tocExportError = useAppSelector(selectReportError);
  const [showTocExportErrorBanner, setShowTocExportErrorBanner] = useState<boolean>(false);

  // Banner shown above the "Project Reports & Exports" dropdown when the user
  // attempts to export Test Data for a project with no Test of Change records.
  const [showNoTocDataBanner, setShowNoTocDataBanner] = useState<boolean>(false);
  const noTocDataBannerRef = useRef<HTMLDivElement | null>(null);
  const noTocDataBannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Warning shown when the user selects "Test Data Charts" but the project
  // has no test data records.
  const [testDataChartsWarning, setTestDataChartsWarning] = useState<string | null>(null);
  const [testDataChartsLoading, setTestDataChartsLoading] = useState<boolean>(false);

  // Dropdown options for "Team Reports & Exports"
  const teamReportOptions = getDetailsContainerTeamReportOptions();

  // Unsaved changes state management
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [pendingProjectNavigation, setPendingProjectNavigation] = useState<{ projectIndex: number; perfId: number; project: any } | null>(null);
  const [showProjectLinkModal, setShowProjectLinkModal] = useState<boolean>(false);
  const [pendingPaginationPage, setPendingPaginationPage] = useState<number | null>(null);
  const [showPaginationModal, setShowPaginationModal] = useState<boolean>(false);

  // Use the unsaved changes prompt hook for breadcrumb and browser navigation
  const {
    showLeaveModal,
    handleNavigation,
    handleLeavePage,
    handleCancelLeave,
  } = useUnsavedChangesPrompt(hasUnsavedChanges, () => {
    setHasUnsavedChanges(false);
  });

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (deleteBannerTimerRef.current) {
        clearTimeout(deleteBannerTimerRef.current);
      }
      if (addTeamBannerTimerRef.current) {
        clearTimeout(addTeamBannerTimerRef.current);
      }
      if (noTocDataBannerTimerRef.current) {
        clearTimeout(noTocDataBannerTimerRef.current);
      }
    };
  }, []);

  // Auto-dismiss TOC export error banner after 5 s and reset Redux slice state
  useEffect(() => {
    if (!tocExportError) return;
    setShowTocExportErrorBanner(true);
    const timer = setTimeout(() => {
      setShowTocExportErrorBanner(false);
      dispatch(clearReportState());
    }, 5000);
    return () => clearTimeout(timer);
  }, [tocExportError, dispatch]);

  // Auto-dismiss the "no test data" warning banner after 5 seconds.
  useEffect(() => {
    if (!testDataChartsWarning) return;
    const timer = setTimeout(() => setTestDataChartsWarning(null), 5000);
    return () => clearTimeout(timer);
  }, [testDataChartsWarning]);

  /**
   * Handler to set unsaved changes state from child tabs
   * @param hasChanges - Whether there are unsaved changes in the form
   */
  const handleSetUnsavedChanges = useCallback((hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges);
  }, []);

  const handleBreadcrumbPrevious = (): void => {
    handleNavigation("/home");
  };
  const { profile } = useProfile();
  const isAdmin = profile?.isAdmin;
  const normalizedProjectAccess = normalizeProjectAccessData(projectAccess);
  const canEditProjects = Boolean(normalizedProjectAccess?.canEdit ?? isAdmin);
  const canShowDeleteActions = canShowDeleteActionsForProject(normalizedProjectAccess, isAdmin);

  // creation modals are handled by the child ManageProjectContainer
  const teamsColumns = teamsInfoColumn;
  const pipListColumn = projectListColumn;

  const itemsPerPage = 10;
  const startIndex = (projectListCurrentPage - 1) * itemsPerPage;
  const location = useLocation();
  const locationState = location.state as {
    teamId?: string | number;
    pipId?: string | number;
    from?: string;
  } | null;
  const stateTeamId = locationState?.teamId;
  const selectedTeamId: string | null =
    stateTeamId == null
      ? teamStorage.getTeamId()
      : String(stateTeamId);
  // pipId is only relevant when navigating from Search Projects — do NOT fall back to
  // sessionStorage, which would cause stale pipId to leak into Select UBT / My Teams flows.
  const selectedPipId: string | null =
    locationState?.pipId == null ? null : String(locationState.pipId);
  console.log("projectId (selectedTeamId):", selectedTeamId);
  console.log("pipId (selectedPipId):", selectedPipId);
  const accessFetchedForTeamRef = useRef<string | null>(null);

  const reportLoading = useAppSelector(selectReportDownloadLoading);
  const pdfDownloadLoading = useAppSelector(isPdfDownloadLoading);
  const projectPdfLoading = useAppSelector(selectProjectPdfLoading);
  const tocPdfLoading = useAppSelector(selectTocPdfLoading);

  /**
   * Handles report/export option selection from the "Team Reports & Exports" dropdown.
   * Supports Team Summary, Team Details (PDF), Export PIP History (CSV), Export PIP Details (CSV),
   * and Export Test of Change (CSV).
   *
   * @param selectedReport - The dropdown option chosen by the user.
   */

  const handleTeamSummaryExport = useCallback(() => {
    dispatch(downloadDisplaySummaryAndDetailsReport({
      reportIdentifier: "teamsummary",
      pageId: "ubtReportIdPip",
      sortColumn: "teamid",
      ...(selectedTeamId ? { teamIdsCd: selectedTeamId } : {}),
      sortOrder: projectListSortDirection.toUpperCase(),
    }));
  }, [dispatch, selectedTeamId, projectListSortDirection]);

  const handleTeamDetailsExport = useCallback(() => {
    dispatch(downloadDisplaySummaryAndDetailsReport({
      reportIdentifier: "teamdetails",
      pageId: "ubtReportIdPip",
      sortColumn: "teamid",
      ...(selectedTeamId ? { teamIdsCd: selectedTeamId } : {}),
      sortOrder: projectListSortDirection.toUpperCase(),
    }));
  }, [dispatch, selectedTeamId, projectListSortDirection]);

  const handlePipHistoryExport = useCallback(() => {
    const nuid = profile?.nuid ? String(profile.nuid) : "";
    if (nuid && selectedTeamId) {
      dispatch(downloadPipHistoryReport({
        nuid,
        pageId: "pipId",
        teamId: selectedTeamId,
      }));
    }
  }, [dispatch, selectedTeamId, profile?.nuid]);

  const handlePipDetailsExport = useCallback(() => {
    const nuid = profile?.nuid ? String(profile.nuid) : "";
    if (nuid) {
      dispatch(downloadPipDetailsReport({
        nuid,
        pageId: "pip",
        teamIds: selectedTeamId,
        sortColumn: "TEAMID",
        sortOrder: projectListSortDirection.toUpperCase() as "ASC" | "DESC",
      }));
    }
  }, [dispatch, selectedTeamId, profile?.nuid, projectListSortDirection]);

  const handleTocPipExport = useCallback(() => {
    dispatch(exportTestOfChangeCSV({
      reportIdentifier: "testofchange",
      pageId: "ubtReportIdPip",
      ...(selectedTeamId ? { teamIdsCd: selectedTeamId } : {}),
      pipSelected: selectedProject?.perfId ? String(selectedProject.perfId) : undefined,
    }));
  }, [dispatch, selectedTeamId, selectedProject]);

  const handleReportExport = useCallback(
    (selectedReport: DropdownOption) => {
      const handlerMap: Record<string, () => void> = {
        [TEAM_SUMMARY_OPTION_ID]: handleTeamSummaryExport,
        [TEAM_DETAILS_OPTION_ID]: handleTeamDetailsExport,
        [EXPORT_PIP_HISTORY_OPTION_ID]: handlePipHistoryExport,
        [EXPORT_PIP_DETAILS_OPTION_ID]: handlePipDetailsExport,
      };
      const handler = handlerMap[selectedReport.id];
      if (handler) {
        handler();
        return;
      }
      if (selectedReport.id === EXPORT_TOC_PIP_OPTION_ID && selectedReport.value === EXPORT_TOC_PIP_OPTION_VALUE) {
        handleTocPipExport();
      }
    },
    [handleTeamSummaryExport, handleTeamDetailsExport, handlePipHistoryExport, handlePipDetailsExport, handleTocPipExport],
  );

  /**
   * Shows the "no Test of Change data" alert banner above the Project Reports
   * dropdown, starts a 5-second auto-dismiss timer, and scrolls the banner into
   * view in non-test environments.
   */
  const triggerNoTocDataBanner = useCallback(() => {
    setShowNoTocDataBanner(true);
    if (noTocDataBannerTimerRef.current) clearTimeout(noTocDataBannerTimerRef.current);
    noTocDataBannerTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) setShowNoTocDataBanner(false);
    }, 5000);
    setTimeout(() => {
      const isTestEnv = !!globalThis?.navigator?.userAgent?.includes('jsdom');
      if (!isTestEnv && noTocDataBannerRef.current) {
        noTocDataBannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }, []);

  /**
   * Fetches test data charts HTML for the given project and opens it in a new tab
   * with an auto-print script injected immediately upon load.
   *
   * @param perfId - The project performance ID to fetch chart data for.
   */
  const handleTestDataChartsExport = useCallback(
    (perfId: number): void => {
      const chartsParams = {
        teamName: teamDetails?.teamName ?? "",
        pipName: selectedProject?.nameOfProject ?? projectDetails?.pipName ?? "",
        numeratorName: projectDetails?.numeratorName ?? "",
        denominatorName: projectDetails?.denominatorName ?? "",
        valueName: projectDetails?.valueName ?? "",
        outsideVarName: projectDetails?.outsideVarName ?? "",
      };
      void (async () => {
        try {
          setTestDataChartsLoading(true);
          const response = await api.get<{ data: ChartData }>(
            endpoints.ubt.teamsReporting.teamsReportingList,
            { params: { perfId } },
          );

          if (response.data.data.tdsList.length === 0) {
            setTestDataChartsWarning(
              `The Project ${chartsParams.pipName} does not have any test data`,
            );
            return;
          }

          setTestDataChartsWarning(null);
          const html = buildHtmlString(response.data.data, chartsParams);

          // Inject an auto-print script so the new tab opens the print
          // dialog immediately, allowing the user to save as PDF.
          // Pattern mirrors PdfReportUtility.ts.
          const scriptBody =
            "window.addEventListener('load',function(){" +
            "window.print();" +
            "window.addEventListener('afterprint',function(){window.close();});" +
            "});";
          const autoPrintScript = "<script>" + scriptBody + "</" + "script>";
          const printReadyHtml = /<\/body>/i.test(html)
            ? html.replace(/<\/body>/i, `${autoPrintScript}</body>`)
            : html + autoPrintScript;

          const blob = new Blob([printReadyHtml], { type: "text/html" });
          const blobUrl = URL.createObjectURL(blob);
          // noopener,noreferrer keeps the child tab in a separate process so
          // its print dialog cannot block the parent application.
          window.open(blobUrl, "_blank", "noopener,noreferrer");
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
        } catch {
          // Silently fail; the user remains on the current page
        } finally {
          setTestDataChartsLoading(false);
        }
      })();
    },
    [teamDetails, selectedProject, projectDetails, setTestDataChartsLoading, setTestDataChartsWarning],
  );

  /**
   * Handles "Project Reports & Exports" dropdown selections.
   * Dispatches the appropriate thunk for each supported report type:
   * - Print Project Records: fetches HTML from the API and opens the print dialog.
   * - Print Test Of Change: guarded by TOC record count; shows alert banner when empty.
   * - Export Test Data (CSV): downloads a test-series CSV filtered to the current project.
   *
   * @param selectedReport - The dropdown option chosen by the user.
   */
  const handleProjectReportExport = useCallback(
    (selectedReport: DropdownOption): void => {
      const nuid = profile?.nuid ? String(profile.nuid) : "";
      const perfId = selectedProject?.perfId ?? projectDetails?.perfId;

      // Handle Print Test Of Change — sortColumn and sortOrder reflect the user's current sort selection.
      // Guard: block if the project has no Test of Change records — nothing to print.
      switch (selectedReport.id) {
        case PRINT_PROJECT_RECORDS_OPTION_ID:
          if (perfId && nuid) {
            dispatch(downloadProjectRecordPDF({ perfId, nuid }));
          }
          break;
        case PRINT_TEST_OF_CHANGE_OPTION_ID:
          if (testOfChangeTotalItems === 0) {
            triggerNoTocDataBanner();
            break;
          }
          if (perfId) {
            dispatch(
              downloadTestOfChangePDF({
                projectId: perfId,
                projectName: selectedProject?.nameOfProject ?? "",
                sortColumn: testOfChangeSortColumn,
                sortOrder: testOfChangeSortDirection.toUpperCase() as "ASC" | "DESC",
              }),
            );
          }
          break;
        case EXPORT_PROJECT_TEST_DATA_OPTION_ID:
          if (perfId && nuid) {
            dispatch(
              downloadTestSeriesByCriteriaReport({
                nuid,
                params: {
                  projectId: String(perfId),
                  sortColumn: testDataSeriesSortColumn ?? "testDate",
                  sortOrder: testDataSeriesSortDirection.toUpperCase(),
                },
              }),
            );
          }
          break;
        case TEST_DATA_CHARTS_OPTION_ID:
          if (perfId) {
            handleTestDataChartsExport(perfId);
          }
          break;
        default:
          break;
      }
    },
    [dispatch, profile?.nuid, selectedProject, projectDetails, testDataSeriesSortColumn, testDataSeriesSortDirection, testOfChangeSortColumn, testOfChangeSortDirection, testOfChangeTotalItems, triggerNoTocDataBanner, handleTestDataChartsExport],
  );

  useEffect(() => {
    if (!selectedTeamId) return;
    if (accessFetchedForTeamRef.current === selectedTeamId) return;
    accessFetchedForTeamRef.current = selectedTeamId;
    dispatch(fetchProjectAccess({ teamId: selectedTeamId, nuid: profile?.nuid ? String(profile.nuid) : null }));
  }, [dispatch, selectedTeamId, profile?.nuid]);

  useEffect(() => {
    if (!selectedTeamId) return;
    dispatch(fetchTeamTrackedStatus({ teamId: selectedTeamId, nuid: profile?.nuid ? String(profile.nuid) : null }));
  }, [dispatch, selectedTeamId, profile?.nuid]);

  const prevTeamIdRef = useRef<string | null | undefined>(undefined);

  // Ensure page scrolls to top whenever navigated/redirected to this route
  useEffect(() => {
    try {
      if (globalThis.window !== undefined && window.scrollTo) {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    } catch (e) {
      console.error("Scroll to top failed:", e);
    }
  }, [location.pathname, selectedTeamId]);

  useEffect(() => {
    if (viewingIndex === null) {
      setViewingIndex(startIndex);
      dispatch(setSelectedProjectIndex(startIndex));
    }
  }, [startIndex, dispatch]);

  useEffect(() => {
    // fetch rating and source options once profile (and nuid) is available
      dispatch(fetchRatingAndSource({ nuid: profile?.nuid ? String(profile.nuid) : null }));
      dispatch(fetchShiftLookup({ nuid: profile?.nuid ? String(profile.nuid) : null }));
  }, [dispatch, profile?.nuid]);

  // When project details are available, fetch related focus/metric/datasource data
  useEffect(() => {
    const regioncd = teamDetails?.kpGlRegionCd;
    console.log('Project details updated, checking if we need to fetch dependent dropdowns. Region:', regioncd);
    const nuid = profile?.nuid ? String(profile.nuid) : null;
    if (!regioncd) return;
    dispatch(fetchFocusAreaList({ regioncd, nuid }));
  }, [projectDetails, profile?.nuid, dispatch, teamDetails]);

  
  const ratingOptions = useMemo(() => normalizeRatingToDropdown(ratings), [ratings]);
  const ideaSourceOptions = useMemo(() => normalizeIdeaToDropdown(ideaSources), [ideaSources]);
  const rawShiftOptions = useAppSelector((s) => selectShiftOptions(s));
  const shiftOptions = normalizeShiftToDropdown(rawShiftOptions ?? []);
  const defaultShiftOption = shiftOptions.find((o) => String(o.value).toLowerCase() === 'all') ?? shiftOptions[1] ?? { id: 0, label: 'Select', value: 'Select' };


  const handleFocusAreaChange = useCallback(async (focusId: any) => {
    const regioncd = teamDetails?.kpGlRegionCd;
    console.log('Handling focus area change, focusId:', focusId, 'regioncd:', regioncd);
    const nuid = profile?.nuid ? String(profile.nuid) : null;
    if (!regioncd || !focusId) return;

    // remember the focusId selected in the ManageProjectContainer
    setSelectedFocusId(String(focusId));

    // Clear the stale valueCompassId from the previous project BEFORE the async
    // fetch. handleMetricChange checks currentValueCompassId and if it is non-null
    // it proceeds directly to fetchDatasourceList without waiting. Clearing it here
    // forces handleMetricChange into the setPendingMetricId path, which safely
    // defers the datasource fetch until fetchFocusAreaValueCompassList has resolved
    // and setCurrentValueCompassId has been called with the correct new value.
    setCurrentValueCompassId(null);

    try {
      const res: any = await dispatch(fetchFocusAreaValueCompassList({ regioncd, focusId, nuid })).unwrap();
      const valueCompassId = normalizeValueCompassResponse(res);
      if (!valueCompassId) return;
      setCurrentValueCompassId(String(valueCompassId));
      await dispatch(fetchMetricList({ regioncd, focusId, valueCompassId, nuid })).unwrap();
    } catch (err) {
      console.warn('Failed to fetch value compass or metrics:', err);
    }
  }, [dispatch, profile, teamDetails]);

  // Helper: resiliently extract a valueCompass id from many possible shapes
  const extractValueCompassId = (item: any) =>
    item?.VALUE_COMPASS_ID ?? item?.VCID ?? item?.vcId ?? item?.valueCompassId ?? item?.VC_ID ?? item?.id ?? item?.value ?? null;

  // Helper: normalize response shapes to a single valueCompass id (or null)
  const normalizeValueCompassResponse = (res: any) => {
    if (!res) return null;
    if (Array.isArray(res) && res.length > 0) return extractValueCompassId(res[0]);
    let list: any[] | null = null;
    if (Array.isArray(res.selectList)) {
      list = res.selectList;
    } else if (Array.isArray(res.items)) {
      list = res.items;
    } else if (Array.isArray(res.list)) {
      list = res.list;
    }
    if (list && list.length > 0) return extractValueCompassId(list[0]);
    return extractValueCompassId(res);
  };

  const handleMetricChange = useCallback(async (metricId: any) => {
    const regioncd = teamDetails?.kpGlRegionCd;
    console.log('Handling metric change, metricId:', metricId, 'regioncd:', regioncd);
    const nuid = profile?.nuid ? String(profile.nuid) : null;
    if (!regioncd || !metricId) return;

    const focusId = selectedFocusId;
    // Only use the explicitly-managed currentValueCompassId. Never fall back to
    // valueCompassList Redux state here: when switching projects, valueCompassList
    // still holds the previous project's data while fetchFocusAreaValueCompassList
    // is in-flight. Reading it would derive the wrong vcId, reintroduce the stale
    // value, and call fetchDatasourceList with an incorrect valueCompassId.
    const valueCompassId = currentValueCompassId;

    if (!focusId || !valueCompassId) {
      setPendingMetricId(metricId);
      return;
    }

    try {
      await dispatch(fetchDatasourceList({ regioncd, focusId, valueCompassId, metricId, nuid })).unwrap();
      setPendingMetricId(null);
    } catch (err) {
      console.warn('Failed to fetch datasource list:', err);
    }
  }, [dispatch, profile, selectedFocusId, currentValueCompassId, teamDetails]);

  useEffect(() => {
    if (!pendingMetricId) return;
    if (!selectedFocusId) return;
    // Only proceed once currentValueCompassId has been explicitly set by
    // handleFocusAreaChange. Do not fall back to valueCompassList, which may
    // still hold the previous project's data during an in-flight fetch.
    if (!currentValueCompassId) return;

    void handleMetricChange(pendingMetricId);
  }, [pendingMetricId, selectedFocusId, currentValueCompassId, handleMetricChange]);

  const handleAddTestData = useCallback(async (payload: any) => {
    const tocId = (payload?.tocId ?? selectedTestOfChange?.tocId ?? selectedTestOfChange?.id) as number | undefined;
    if (typeof tocId !== "number") {
      // eslint-disable-next-line no-alert
      alert("Invalid Test of Change ID.");
      return { success: false, error: "Invalid TOC ID" };
    }

    try {
      // If payload contains a seriesId, treat this as an update
      const seriesId = payload?.seriesId ?? payload?.tdsId ?? payload?.id ?? null;
      if (seriesId == null) {
        await dispatch(addTestDataSeries({ tocId, payload, nuid: profile?.nuid ? String(profile.nuid) : null })).unwrap();
      } else {
        await dispatch(updateTestDataSeries({ tocId, seriesId, payload, nuid: profile?.nuid ? String(profile.nuid) : null })).unwrap();
      }
      // Always go back to page 1 after an add/edit so the new record is visible
      // and we avoid a race between this explicit fetch and the tab's own
      // onSetTestDataSeriesPage(1) call (which was previously fetching a different page).
      dispatch(setTestDataSeriesCurrentPage(1));
      try {
        const fetchPayload: any = {
          tocId,
          page: 1,
          perPage: testDataSeriesPerPage,
          sortColumn: testDataSeriesSortColumn ?? 'testDate',
          countOnly: false,
          sortOrder: testDataSeriesSortDirection.toUpperCase() as 'ASC' | 'DESC',
        };
        dispatch(fetchTestDataSeries(fetchPayload));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Failed to refresh test data series after add:", err);
      }
      return { success: true };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to add test data series:", err);
      return { success: false, error: err };
    }
  }, [dispatch, selectedTestOfChange, testDataSeriesPerPage, testDataSeriesSortColumn, testDataSeriesSortDirection]);

  const handleAddTestOfChange = useCallback(async (payload: any) => {
    const projectId = payload?.projectId ?? selectedProject?.perfId;
    if (typeof projectId !== "number") {
      // eslint-disable-next-line no-alert
      alert("Invalid project ID.");
      return { success: false, error: "Invalid project ID" };
    }

    try {
      const res: any = await dispatch(addTestOfChange({ projectId, payload })).unwrap();
      const created = res?.data ?? res;

      // Ensure we're on page 1 and re-fetch canonical server data so fields like
      // ratingText and dateLastUpd are populated as the server computes them.
      dispatch(setTestsOfChangeCurrentPage(1));
      const fetchPayload: any = {
        projectId,
        page: 1,
        perPage: testOfChangePerPage,
        sortColumn: testOfChangeSortColumn ?? 'datelastupd',
        countOnly: false,
        sortOrder: testOfChangeSortDirection.toUpperCase() as 'ASC' | 'DESC',
      };

      try {
        const listRes: any = await dispatch(fetchTestsOfChange(fetchPayload)).unwrap();
        const items: any[] = listRes?.items ?? listRes?.data ?? (Array.isArray(listRes) ? listRes : []);
        // Try to find the created item in the returned set (match by tocId or id)
        let found = null;
        if (created && items?.length) {
          const createdId = created?.tocId ?? created?.id;
          found = items.find((it: any) => (it.tocId ?? it.id) === createdId) ?? null;
        }
        // Select the created item (prefer the server-returned version)
        if (found) setSelectedTestOfChange(found);
        else if (created) setSelectedTestOfChange(created);

        // Activate the Test of Change tab so the updated table is visible
        setActiveTabId(2);
      } catch (err) {
        if (created) setSelectedTestOfChange(created);
        setActiveTabId(2);
        console.error("Failed to fetch tests of change after adding new one:", err);
      }

      return { success: true, created };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to add test of change:", err);
      return { success: false, error: err };
    }
  }, [dispatch, selectedProject]);

  /**
   * Handle adding a team to "My Teams"
   * Dispatches the addTeamToMyTeams thunk and shows success/error banner
   */
  const handleAddTeamToMyTeams = useCallback(async () => {
    if (!selectedTeamId) {
      setAddTeamBannerMessage("Invalid team ID");
      setAddTeamBannerVariant("Error");
      setShowAddTeamBanner(true);
      return;
    }

    try {
      await dispatch(
        addTeamToMyTeams({
          teamId: selectedTeamId,
          nuid: profile?.nuid ? String(profile.nuid) : null,
        }),
      ).unwrap();

      // Show success banner
      setAddTeamBannerMessage("Team has been added to your My Teams page");
      setAddTeamBannerVariant("Success");
      setShowAddTeamBanner(true);

      // Auto-hide banner after 5 seconds
      if (addTeamBannerTimerRef.current) {
        clearTimeout(addTeamBannerTimerRef.current);
      }
      addTeamBannerTimerRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          setShowAddTeamBanner(false);
        }
      }, 5000);

      // Refresh the isTeamTracked status so the link disappears
      dispatch(
        fetchTeamTrackedStatus({
          teamId: selectedTeamId,
          nuid: profile?.nuid ? String(profile.nuid) : null,
        }),
      );
    } catch (err: any) {
      // Extract error message from response or use generic message
      const errorMessage = err?.message ?? err?.error ?? "Failed to add team to my teams";
      setAddTeamBannerMessage(errorMessage);
      setAddTeamBannerVariant("Error");
      setShowAddTeamBanner(true);

      // Auto-hide error banner after 5 seconds
      if (addTeamBannerTimerRef.current) {
        clearTimeout(addTeamBannerTimerRef.current);
      }
      addTeamBannerTimerRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          setShowAddTeamBanner(false);
        }
      }, 5000);
    }
  }, [dispatch, selectedTeamId, profile?.nuid]);

  const handleDeleteTestDataSeries = useCallback(async (seriesId: number, tocId?: number) => {
    const resolvedTocId = tocId ?? selectedTestOfChange?.tocId ?? selectedTestOfChange?.id;

    if (typeof seriesId !== 'number') {
      // eslint-disable-next-line no-alert
      alert('Invalid test data series ID.');
      return { success: false, error: 'Invalid seriesId' };
    }

    if (typeof resolvedTocId !== 'number') {
      // eslint-disable-next-line no-alert
      alert('Invalid project/TOC ID for deleting test data series.');
      return { success: false, error: 'Invalid tocId' };
    }

    try {
      await dispatch(deleteTestDataSeries({ tocId: resolvedTocId, seriesId, nuid: profile?.nuid ? String(profile.nuid) : null })).unwrap();

      // After deletion the total drops by 1. Clamp the page so we never request
      // an empty page (e.g. page 2 when only 1 item existed there).
      const newTotal = Math.max(0, testDataSeriesTotalItems - 1);
      const maxValidPage = Math.max(1, Math.ceil(newTotal / testDataSeriesPerPage));
      const pageToFetch = Math.min(testDataSeriesCurrentPage, maxValidPage);

      if (pageToFetch !== testDataSeriesCurrentPage) {
        dispatch(setTestDataSeriesCurrentPage(pageToFetch));
      }

      // Refresh list for the corrected page
      const fetchPayload: any = {
        tocId: resolvedTocId,
        page: pageToFetch,
        perPage: testDataSeriesPerPage,
        sortColumn: testDataSeriesSortColumn ?? 'testDate',
        countOnly: false,
        sortOrder: testDataSeriesSortDirection.toUpperCase() as 'ASC' | 'DESC',
      };
      try {
        dispatch(fetchTestDataSeries(fetchPayload));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to refresh test data series after delete:', err);
      }

      return { success: true };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete test data series:', err);
      return { success: false, error: err };
    }
  }, [dispatch, selectedTestOfChange, selectedProject, projectDetails, testDataSeriesCurrentPage, testDataSeriesPerPage, testDataSeriesSortColumn, testDataSeriesSortDirection, testDataSeriesTotalItems]);

  const handleEditTestOfChange = useCallback(async (tocId: number, payload: any) => {
    const projectId = selectedProject?.perfId ?? projectDetails?.perfId;
    if (typeof projectId !== 'number') {
      // eslint-disable-next-line no-alert
      alert('Invalid project ID for editing.');
      return { success: false, error: 'Invalid project ID' };
    }

    if (typeof tocId !== 'number') {
      // eslint-disable-next-line no-alert
      alert('Invalid Test of Change ID for editing.');
      return { success: false, error: 'Invalid tocId' };
    }

    try {
      const res: any = await dispatch(updateTestOfChange({ projectId, tocId, payload, nuid: profile?.nuid ? String(profile.nuid) : null })).unwrap();
      const updated = res?.data ?? res;

      // Refresh list for current project/page
      const fetchPayload: any = {
        projectId,
        page: testOfChangeCurrentPage,
        perPage: testOfChangePerPage,
        sortColumn: testOfChangeSortColumn ?? 'datelastupd',
        countOnly: false,
        sortOrder: testOfChangeSortDirection.toUpperCase() as 'ASC' | 'DESC',
      };
      try {
        const listRes: any = await dispatch(fetchTestsOfChange(fetchPayload)).unwrap();
        const items: any[] = listRes?.items ?? listRes?.data ?? (Array.isArray(listRes) ? listRes : []);
        // Try to find the updated item in the returned set
        let found = null;
        if (updated && items?.length) {
          const updatedId = updated?.tocId ?? updated?.id ?? tocId;
          found = items.find((it: any) => (it.tocId ?? it.id) === updatedId) ?? null;
        }
        if (found) setSelectedTestOfChange(found);
        else if (updated) setSelectedTestOfChange(updated);
        setActiveTabId(2);
      } catch (err) {
        if (updated) setSelectedTestOfChange(updated);
        setActiveTabId(2);
        console.error('Failed to fetch tests of change after editing:', err);
      }

      return { success: true, updated };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to edit test of change:', err);
      return { success: false, error: err };
    }
  }, [dispatch, selectedProject, projectDetails, testOfChangeCurrentPage, testOfChangePerPage, testOfChangeSortColumn, testOfChangeSortDirection]);

  // Fetch and reset tests of change when selected project or paging/sort changes
  useEffect(() => {
    if (selectedProject?.perfId) {
      const payload: any = {
        projectId: selectedProject.perfId,
        page: testOfChangeCurrentPage,
        perPage: testOfChangePerPage,
        sortColumn: testOfChangeSortColumn ?? 'datelastupd',
        countOnly: false,
        sortOrder: testOfChangeSortDirection.toUpperCase() as 'ASC' | 'DESC',
      };
      // eslint-disable-next-line no-console
      dispatch(fetchTestsOfChange(payload));
    }
  }, [selectedProject, testOfChangeCurrentPage, testOfChangePerPage, dispatch, testOfChangeSortColumn, testOfChangeSortDirection, setSelectedTestOfChange]);

  // Reset tests-of-change state and selection only when the selected project changes.
  useEffect(() => {
    // Only perform reset when project actually changes (non-null)
    if (!selectedProject) return;
    setSelectedTestOfChange(null);
    // Clear the "no test-of-change data" export banner for the previous project.
    setShowNoTocDataBanner(false);
    // Resetting the slice clears stale list and paging; set page to 1.
    dispatch(resetTestsOfChangeState());
    dispatch(setTestsOfChangeCurrentPage(1));
  }, [selectedProject, dispatch]);

  // Fetch test data series when selectedTestOfChange changes or paging/sort changes
  useEffect(() => {
    if (selectedTestOfChange?.tocId) {
      const payload: any = {
        tocId: selectedTestOfChange?.tocId,
        page: testDataSeriesCurrentPage,
        perPage: testDataSeriesPerPage,
        sortColumn: testDataSeriesSortColumn ?? 'testDate',
        countOnly: false,
        sortOrder: testDataSeriesSortDirection.toUpperCase() as 'ASC' | 'DESC',
      };
      dispatch(fetchTestDataSeries(payload));
    }
  }, [testDataSeriesCurrentPage, testDataSeriesPerPage, dispatch, testDataSeriesSortColumn, testDataSeriesSortDirection, selectedTestOfChange]);


  // When the project list finishes loading for the first time, select the matching project.
  // If a pipId was passed (navigation from Search Projects), find and select that specific
  // project. Otherwise fall back to the first project in the list.
  useEffect(() => {
    if (projectListLoading || !Array.isArray(projectList) || projectList.length === 0 || selectedProject) return;

    const targetProject = resolvePipIdTargetProject(projectList, selectedPipId, startIndex);
    if (!targetProject.project) return;

    const { project, listIndex } = targetProject;
    const absoluteIndex = startIndex + listIndex;

    setSelectedProject(project);
    dispatch(setSelectedProjectIndex(absoluteIndex));
    setViewingIndex(absoluteIndex);
    dispatch(setTestsOfChangeCurrentPage(1));
    if (project.perfId) {
      dispatch(fetchProjectDetails({ projectid: project.perfId, nuid: profile?.nuid ? String(profile.nuid) : null }) as any);
    }
  }, [projectListLoading, projectList, selectedProject, startIndex, selectedPipId, dispatch, profile]);
  //fetch project List
  useEffect(() => {
    // If the selected team changed since last fetch, clear stale pip list state
    // and selected project so we don't auto-select a project from the previous team.
    // This must happen BEFORE building the payload so we always start at page 1
    // for a new team — projectListCurrentPage in the selector is still stale at
    // this point and resetPipListState() won't reflect until the next render.
    const teamChanged = prevTeamIdRef.current !== selectedTeamId;
    if (teamChanged) {
      prevTeamIdRef.current = selectedTeamId;
      dispatch(resetPipListState());
      setSelectedProject(null);
      setViewingIndex(null);
    }

    const payload: any = {
      page: teamChanged ? 1 : projectListCurrentPage,
      perPage: projectListPerPage,
      sortType: projectListSortColumn,
      sortOrder: projectListSortDirection.toUpperCase() as 'ASC' | 'DESC',
      teamId: selectedTeamId,
      ...(teamChanged && selectedPipId ? { pipId: selectedPipId } : {}),
    };

    dispatch(fetchPipList(payload));
  }, [projectListCurrentPage, projectListPerPage, dispatch, projectListSortColumn, projectListSortDirection, selectedTeamId]);

  //fetch team members
  useEffect(() => {
    dispatch(fetchTeamMembers({ teamId: String(selectedTeamId) }));
  }, [dispatch, selectedTeamId]);

  const handlePageChange = useCallback((page: number) => {
    // Check for unsaved changes before allowing pagination
    if (hasUnsavedChanges) {
      setPendingPaginationPage(page);
      setShowPaginationModal(true);
      return;
    }

    // When changing pages, mark that we should select the first project
    // after the refreshed list arrives, then update the page which triggers fetch
    const pageStartIndex = (page - 1) * itemsPerPage;
    // Immediately set viewing/selected index so UI highlights the first row for the new page
    setViewingIndex(pageStartIndex);
    dispatch(setSelectedProjectIndex(pageStartIndex));
    // Also mark pending fetch flow so we load the canonical project details when list arrives
    setPendingSelectFirstOnListUpdate(true);
    listRefreshStartedRef.current = false;
    dispatch(setProjectDetailCurrentPage(page));
  }, [dispatch, hasUnsavedChanges]);

  /**
   * Handle confirmation of pagination navigation when there are unsaved changes
   */
  const handleConfirmPaginationNavigation = useCallback(() => {
    if (pendingPaginationPage !== null) {
      setShowPaginationModal(false);
      setHasUnsavedChanges(false);
      
      // Execute the pagination change
      const page = pendingPaginationPage;
      const pageStartIndex = (page - 1) * itemsPerPage;
      setViewingIndex(pageStartIndex);
      dispatch(setSelectedProjectIndex(pageStartIndex));
      setPendingSelectFirstOnListUpdate(true);
      listRefreshStartedRef.current = false;
      dispatch(setProjectDetailCurrentPage(page));
      
      setPendingPaginationPage(null);
    }
  }, [pendingPaginationPage, dispatch]);

  /**
   * Handle cancellation of pagination navigation
   */
  const handleCancelPaginationNavigation = useCallback(() => {
    setShowPaginationModal(false);
    setPendingPaginationPage(null);
  }, []);

  const handleDeleteClick = useCallback((selectedRowOrId: any, deleteText: string) => {
    if (!canShowDeleteActions) {
      return;
    }

    let obj: any = null;
    if (selectedRowOrId && typeof selectedRowOrId === 'object') {
      obj = selectedRowOrId;
    } else {
      const id = Number(selectedRowOrId);
      if (!Number.isNaN(id)) {
        obj = projectList?.find((p: any) => String(p.perfId) === String(id)) ?? null;
      }
    }

    const rawId = obj?.perfId ?? selectedRowOrId;
    const idNum = Number(selectedTeamId);

    const isTeamDelete = (typeof deleteText === 'string' && deleteText.toLowerCase().includes('team')) || !!obj?.teamId;

    if (isTeamDelete) {
      setCurrentTeamIdToDelete(idNum);
      setCurrentProjectIdToDelete(null);
    } else {
      setCurrentProjectIdToDelete(rawId);
      setCurrentTeamIdToDelete(null);
    }

    setResponseForms([
      {
        id: String(idNum),
        title: obj?.nameOfProject ?? obj?.teamName ?? "",
        subject: "",
        message: "",
      },
    ]);
    if (isTeamDelete) {
      if (typeof setShowTeamDeleteModel === 'function') setShowTeamDeleteModel(true);
      else if (typeof setShowDeleteModal === 'function') setShowDeleteModal(true);
    } else if (typeof setShowDeleteModal === 'function') setShowDeleteModal(true);
  }, [canShowDeleteActions, projectList, selectedTeamId]);

  const handleCancelDelete = useCallback(() => {
    if (typeof setShowDeleteModal === 'function') setShowDeleteModal(false);
    if (typeof setShowTeamDeleteModel === 'function') setShowTeamDeleteModel(false);
    setCurrentProjectIdToDelete(null);
    setCurrentTeamIdToDelete(null);
  }, []);

  const clearDeleteRelatedState = useCallback((resetForceTabs: boolean) => {
    dispatch(resetPipListState());
    dispatch(resetProjectDetails());
    dispatch(resetTestsOfChangeState());
    dispatch(resetTestDataSeriesState());

    if (isMountedRef.current) {
      setSelectedProject(null);
      setSelectedTestOfChange(null);
      setActiveTabId(undefined);
      setViewingIndex(null);
      if (resetForceTabs) {
        setForceShowTabs(false);
      }
    }
  }, [dispatch]);

  const buildProjectListPayload = useCallback((nuid: string | null) => ({
    page: 1,
    perPage: projectListPerPage,
    sortType: projectListSortColumn,
    sortOrder: projectListSortDirection.toUpperCase() as 'ASC' | 'DESC',
    teamId: selectedTeamId,
    nuid,
  }), [projectListPerPage, projectListSortColumn, projectListSortDirection, selectedTeamId]);

  const refreshAfterProjectDelete = useCallback(async (nuid: string | null) => {
    try {
      const listRes: any = await dispatch(fetchPipList(buildProjectListPayload(nuid))).unwrap();
      const items: any[] = listRes?.items ?? listRes?.data ?? (Array.isArray(listRes) ? listRes : []);
      const first = items?.length ? items[0] : null;

      if (!first) {
        clearDeleteRelatedState(true);
        return;
      }

      setSelectedProject(first);
      setViewingIndex(0);
      dispatch(setSelectedProjectIndex(0));
      if (isMountedRef.current) {
        setForceShowTabs(false);
      }

      if (!first?.perfId) return;
      try {
        await dispatch(fetchProjectDetails({ projectid: first.perfId, nuid }) as any).unwrap();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch project details for first project after delete:', e);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to refresh project list after delete:', e);
      clearDeleteRelatedState(true);
    }
  }, [dispatch, buildProjectListPayload, clearDeleteRelatedState]);

  const refreshAfterTeamDelete = useCallback(async (nuid: string | null) => {
    try {
      clearDeleteRelatedState(false);
      await Promise.all([
        dispatch(fetchTeamMembers({ teamId: String(selectedTeamId) })).unwrap().catch(() => null),
        dispatch(fetchPipList(buildProjectListPayload(nuid))).unwrap().catch(() => []),
      ]);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to refresh team members after team delete:', e);
    }
  }, [dispatch, selectedTeamId, buildProjectListPayload, clearDeleteRelatedState]);

  const showDeleteSuccessBanner = useCallback((deletedName: string, isTeamDelete: boolean) => {
    if (!isMountedRef.current) return;

    setDeletedResponseName(deletedName);
    setDeleteBannerPlacement(isTeamDelete ? 'summary' : 'detail');
    setDelayNoProjectBannerUntilDeleteBannerEnds(!isTeamDelete);
    setShowDeleteBanner(true);
    if (deleteBannerTimerRef.current) {
      clearTimeout(deleteBannerTimerRef.current);
    }
    deleteBannerTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setShowDeleteBanner(false);
        setDelayNoProjectBannerUntilDeleteBannerEnds(false);
      }
    }, 5000);
  }, [setDeletedResponseName, setShowDeleteBanner]);

  const resetDeleteFormState = useCallback(() => {
    if (!isMountedRef.current) return;
    setResponseForms([{ id: '1', title: '', subject: '', message: '' }]);
  }, [setResponseForms]);

  const executeDeleteAction = useCallback(async (
    teamIdToDelete: number | null,
    projectIdToDelete: number | null,
    nuid: string | null,
  ) => {
    if (teamIdToDelete) {
      await dispatch(deleteMyTeam({ id: teamIdToDelete, nuid })).unwrap();
      return;
    }

    if (projectIdToDelete) {
      await dispatch(deleteProject({ projectid: projectIdToDelete, nuid })).unwrap();
    }
  }, [dispatch]);

  const handleConfirmDelete = useCallback(async () => {
    if (!currentProjectIdToDelete && !currentTeamIdToDelete) return;
    const errorPlacement = ({ true: "summary", false: "detail" } as const)[
      String(Boolean(currentTeamIdToDelete)) as "true" | "false"
    ];
    const isTeamDelete = Boolean(currentTeamIdToDelete);

    // Close modal immediately so UI does not block while async delete runs
    if (typeof setShowDeleteModal === 'function') setShowDeleteModal(false);
    if (typeof setShowTeamDeleteModel === 'function') setShowTeamDeleteModel(false);
    setIsDeleteFlowLoading(true);

    const deletedResponseNameLocal = responseForms[0]?.title ?? '';
    const nuid = profile?.nuid ? String(profile?.nuid) : null;

    try {
      await executeDeleteAction(currentTeamIdToDelete, currentProjectIdToDelete, nuid);

      if (isTeamDelete) {
        // Navigate to landing page — success banner is shown there
        resetDeleteFormState();
        navigate('/home', { state: { teamDeleteSuccess: true } });
        return;
      }

      showDeleteSuccessBanner(deletedResponseNameLocal, isTeamDelete);
      resetDeleteFormState();
      await refreshAfterProjectDelete(nuid);

      if (isMountedRef.current) {
        setCurrentProjectIdToDelete(null);
        setCurrentTeamIdToDelete(null);
      }
    } catch (err) {
      const msg =
        (typeof err === "string" ? err : (err as any)?.message) ??
        "Failed to delete. Please try again.";
      try {
        displayErrorBanner(msg, errorPlacement);
      } catch {}
    } finally {
      if (isMountedRef.current) {
        setIsDeleteFlowLoading(false);
      }
    }
  }, [
    currentProjectIdToDelete,
    currentTeamIdToDelete,
    responseForms,
    profile?.nuid,
    displayErrorBanner,
    executeDeleteAction,
    showDeleteSuccessBanner,
    resetDeleteFormState,
    refreshAfterTeamDelete,
    refreshAfterProjectDelete,
  ]);


  //handle viewDetail click - fetch project details

  /**
   * Handles viewing project details, focusing Project Details tab in Safari.
   * @param rowIndex - Index of the row
   * @param pipID - Project perfId
   * @param selectedProject - Project object
   */
  const handleViewProjectDetails = useCallback(async (rowIndex: number, pipID: number, selectedProject: any) => {
    if (!pipID) {
      return;
    }

    try {
      const nuid = profile?.nuid ? String(profile.nuid) : null;
      await dispatch(
        fetchProjectDetails({ projectid: pipID, nuid }) as any,
      ).unwrap();

      handleViewDetails(rowIndex);
      setSelectedProject(selectedProject);

      // Safari-only: focus and smooth scroll Project Details tab
      setTimeout(() => {
        const isSafari = /^((?!chrome|android).)*safari/i.test(globalThis.navigator.userAgent);
        // Skip scroll/focus in test environment to avoid infinite timers
        const isTestEnv = !!globalThis?.navigator?.userAgent?.includes('jsdom');
        if (isSafari && !isTestEnv && tabsRef.current) {
          tabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 0);
    } catch (error) {
      console.error("Error fetching project details:", error);
    }
  }, [dispatch, profile]);

  /**
   * Handler for project name link clicks - checks for unsaved changes
   * @param projectIndex - Index of the project in the list
   * @param perfId - Project ID
   * @param project - Project object
   */
  const handleProjectNameClick = useCallback(
    (projectIndex: number, perfId: number, project: any) => {
      if (hasUnsavedChanges) {
        setPendingProjectNavigation({ projectIndex, perfId, project });
        setShowProjectLinkModal(true);
      } else {
        handleViewProjectDetails(projectIndex, perfId, project);
      }
    },
    [hasUnsavedChanges, handleViewProjectDetails],
  );

  /**
   * Handler for confirming navigation when there are unsaved changes
   */
  const handleConfirmProjectNavigation = useCallback(() => {
    setShowProjectLinkModal(false);
    setHasUnsavedChanges(false);
    
    if (pendingProjectNavigation) {
      const { projectIndex, perfId, project } = pendingProjectNavigation;
      handleViewProjectDetails(projectIndex, perfId, project);
      setPendingProjectNavigation(null);
    }
  }, [pendingProjectNavigation, handleViewProjectDetails]);

  /**
   * Handler for cancelling navigation when there are unsaved changes
   */
  const handleCancelProjectNavigation = useCallback(() => {
    setShowProjectLinkModal(false);
    setPendingProjectNavigation(null);
  }, []);

  const showOperationSuccessBanner = useCallback((res: any, fallbackMessage: string) => {
    try {
      const msg = res?.message ?? fallbackMessage;
      showSuccessBanner(msg);
    } catch {}
  }, [showSuccessBanner]);

  const refreshProjectListAndSelectFirst = useCallback(async (nuid: string | null) => {
    try {
      const listPayload: any = {
        page: 1,
        perPage: projectListPerPage,
        sortType: projectListSortColumn,
        sortOrder: projectListSortDirection.toUpperCase() as 'ASC' | 'DESC',
        teamId: selectedTeamId,
        nuid,
      };
      const listRes: any = await dispatch(fetchPipList(listPayload)).unwrap();
      const items: any[] = listRes?.items ?? listRes?.data ?? (Array.isArray(listRes) ? listRes : []);
      const first = items?.length ? items[0] : null;
      if (first) {
        setSelectedProject(first);
        setViewingIndex(0);
        dispatch(setSelectedProjectIndex(0));
        try {
          if (first?.perfId) {
            await dispatch(fetchProjectDetails({ projectid: first.perfId, nuid })).unwrap();
          }
        } catch (e) {
          // non-fatal: continue
          // eslint-disable-next-line no-console
          console.warn('Failed to fetch project details for first project after save:', e);
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to refresh project list after save:', e);
    }
  }, [dispatch, projectListPerPage, projectListSortColumn, projectListSortDirection, selectedTeamId]);

  const getApiErrorBannerMessage = useCallback((error: unknown, fallbackMessage: string) => {
    const e = error as any;
    const responseDetailsMessage = Array.isArray(e?.response?.data?.details)
      ? e.response.data.details.find((detail: any) => typeof detail?.message === 'string')?.message
      : undefined;
    const payloadDetailsMessage = Array.isArray(e?.payload?.details)
      ? e.payload.details.find((detail: any) => typeof detail?.message === 'string')?.message
      : undefined;

    if (typeof error === 'string') {
      return error;
    }

    return (
      responseDetailsMessage ??
      e?.response?.data?.message ??
      e?.response?.data?.error ??
      e?.response?.data?.detail ??
      (typeof e?.payload === 'string' ? e.payload : undefined) ??
      payloadDetailsMessage ??
      e?.payload?.message ??
      e?.payload?.error ??
      e?.payload?.detail ??
      e?.error?.message ??
      e?.message ??
      fallbackMessage
    );
  }, []);



  /**
   * Handles project creation and focuses Project Details tab in Safari after success.
   */
  const handleCreateProject = useCallback(async (payload: any) => {
    const nuid = profile?.nuid ? String(profile.nuid) : null;
    try {
      const payloadWithTeam = { ...payload, teamId: payload?.teamId ?? (selectedTeamId ? Number(selectedTeamId) : selectedTeamId) };
      const res: any = await dispatch(createProject({ payload: payloadWithTeam, nuid })).unwrap();
      await refreshProjectListAndSelectFirst(nuid);
      showOperationSuccessBanner(res, 'Project created successfully.');

      // Reset active tab to show Project Details after creation
      setActiveTabId(undefined);
      setHasUnsavedChanges(false);

      // Safari-only: focus and smooth scroll Project Details tab
      setTimeout(() => {
        const isSafari = /^((?!chrome|android).)*safari/i.test(globalThis.navigator.userAgent);
        const isTestEnv = !!globalThis?.navigator?.userAgent?.includes('jsdom');
        if (isSafari && !isTestEnv && tabsRef.current) {
          tabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 0);

      return res;
    } catch (err) {
      console.error('Failed to create project:', err);
      const msg = getApiErrorBannerMessage(err, 'Failed to create project. Please try again.');
      try {
        displayManageProjectErrorBanner(msg as string);
      } catch {}
      throw err;
    }
  }, [dispatch, profile?.nuid, selectedTeamId, showOperationSuccessBanner, refreshProjectListAndSelectFirst, displayManageProjectErrorBanner, getApiErrorBannerMessage]);


  /**
   * Handles project update and focuses Project Details tab in Safari after success.
   */
  const handleUpdateProject = useCallback(async (payload: any) => {
    // payload is the mapped payload from ManageProjectContainer; must include projectId or we derive it
    const nuid = profile?.nuid ? String(profile.nuid) : null;
    const projectId = payload?.projectId ?? payload?.perfId ?? selectedProject?.perfId ?? projectDetails?.perfId;
    if (!projectId) {
      const err = new Error('No projectId available for update');
      console.error(err);
      throw err;
    }
    try {
      const payloadWithTeam = { ...payload, teamId: payload?.teamId ?? (selectedTeamId ? Number(selectedTeamId) : selectedTeamId) };
      const res: any = await dispatch(updateProject({ projectId, payload: payloadWithTeam, nuid })).unwrap();

      // Reset activeTabId BEFORE refreshing so that when Tabs remounts
      // (after projectDetailsLoading resolves) the first tab is active.
      setActiveTabId(undefined);
      setHasUnsavedChanges(false);

      await refreshProjectListAndSelectFirst(nuid);
      showOperationSuccessBanner(res, 'Project updated successfully.');

      // Safari-only: focus and smooth scroll Project Details tab
      setTimeout(() => {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari && tabsRef.current) {
          tabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 0);

      return res;
    } catch (err) {
      console.error('Failed to update project:', err);
      const msg = getApiErrorBannerMessage(err, 'Failed to update project. Please try again.');
      try {
        displayManageProjectErrorBanner(msg as string);
      } catch {}
      throw err;
    }
  }, [dispatch, profile?.nuid, selectedProject, projectDetails, selectedTeamId, showOperationSuccessBanner, refreshProjectListAndSelectFirst, displayManageProjectErrorBanner, getApiErrorBannerMessage]);



  const handleViewDetails = (rowIndex: number) => {
    setViewingIndex(rowIndex);
    dispatch(setSelectedProjectIndex(rowIndex));
    setTimeout(() => {
      const isTestEnv = !!globalThis?.navigator?.userAgent?.includes('jsdom');
      if (!isTestEnv) {
        tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 0);
  };

  const getDeleteButton = (object: any, deleteText: string = 'Delete') => (
    <button
      type="button"
      onClick={() => handleDeleteClick(object, deleteText)}
      className="bg-transparent border-0 p-0 cursor-pointer"
      aria-label={deleteText}
    >
      <img
        src="/assets/images/trash-outline.svg"
        alt="Delete icon"
        className="min-w-6 min-h-6"
        width={24}
        height={24}
        style={{ display: 'block' }}
      />
    </button>
  );

  const handleProjectListSort = useCallback(
    (columnKey: string, direction: "asc" | "desc") => {
      const apiColumnMap: { [key: string]: string } = {
        'status': 'status',
        'lastUpdated': 'lastUpdated',
        'startDate': 'startDate',
      };
      const apiSortColumn = apiColumnMap[columnKey] ?? columnKey.toUpperCase();
      setProjectListUiSortColumn(columnKey);
      setProjectListSortColumn(apiSortColumn);
      setProjectListSortDirection(direction);
      // Reset to page 1 when sorting changes and mark that we should
      // select the first project after the refreshed list arrives.
      setPendingSelectFirstOnListUpdate(true);
      dispatch(setProjectDetailCurrentPage(1));
    },
    [dispatch],
  );

  // When a sort triggers a list refresh, select the first project from
  // the refreshed page and fetch its details.
  useEffect(() => {
    if (!pendingSelectFirstOnListUpdate) return;

    // If the list refresh hasn't started yet, wait until we see loading=true
    if (!listRefreshStartedRef.current) {
      if (projectListLoading) {
        listRefreshStartedRef.current = true;
      }
      return;
    }

    // Wait for the refresh to complete
    if (projectListLoading) return;

    if (!Array.isArray(projectList) || projectList.length === 0) {
      listRefreshStartedRef.current = false;
      setPendingSelectFirstOnListUpdate(false);
      return;
    }

    const pageStartIndex = ((projectListCurrentPage ?? 1) - 1) * (projectListPerPage ?? itemsPerPage);
    const firstProject = projectList[0];
    if (firstProject) {
      setSelectedProject(firstProject);
      setViewingIndex(pageStartIndex);
      dispatch(setSelectedProjectIndex(pageStartIndex));
      // reset tests-of-change paging when selecting a new project
      dispatch(setTestsOfChangeCurrentPage(1));
      if (firstProject.perfId) {
        const nuid = profile?.nuid ? String(profile.nuid) : null;
        dispatch(fetchProjectDetails({ projectid: firstProject.perfId, nuid }) as any);
      }
    }

    listRefreshStartedRef.current = false;
    setPendingSelectFirstOnListUpdate(false);
  }, [pendingSelectFirstOnListUpdate, projectListLoading, projectList, projectListCurrentPage, projectListPerPage, dispatch, profile]);

  const teamRowDetail: TableRow[] = teamDetails ? [
    {
      rowKey: `detail-${teamDetails.teamId}`,
      id: teamDetails.teamId,
      deleteIcon: canShowDeleteActions ? getDeleteButton(teamDetails, 'Delete team') : null,
      ubtName: (
            <div className="max-w-[115px] whitespace-normal break-words overflow-wrap-[anywhere]">
              {teamDetails.teamName ?? ""}
            </div>
          ),
      dateTeamEstablished: teamDetails.teamDateCreated ?? '',
      teamRating: teamDetails.teamRating ?? '',
      serviceType: teamDetails.serviceDeliveryType ?? '',
      usingHuddles: formatBooleanField(teamDetails.usingHuddles),
      defaultMembership: formatBooleanField(teamDetails.defaultMembership),
    },
  ] : [];

  // Extract deeply nested button rendering to a separate function to avoid deep nesting
  const handleViewDetailsButtonClick = useCallback(
    (rowIndex: number, perfId: number, project: any) => {
      handleViewProjectDetails(rowIndex, perfId, project);
    },
    [handleViewProjectDetails],
  );

  const renderViewDetailsButton = (
    rowIndex: number,
    perfId: number,
    project: any
  ) => {
    const isActive = isProjectActive(rowIndex, perfId, selectedProjectIndex, viewingIndex, selectedProject);
    return (
      <button
        type="button"
        data-active={isActive}
        className={
          isActive
            ? "button py-0! bg-[#003B71]! text-white! hover:bg-[#003B71]! hover:text-white! btn-secondary"
            : "button py-0! btn-primary hover:bg-[#003B71]! hover:text-white!"
        }
        onClick={() => handleViewDetailsButtonClick(rowIndex, perfId, project)}
      >
        {isActive ? "Viewing" : "View details"}
      </button>
    );
  };

  const renderProjectNameButton = useCallback(
    (rowIndex: number, perfId: number, projectName: string, project: any) => (
      <button
        type="button"
        className="-medium text-kp-blue-dark! underline bg-transparent border-0 p-0 text-left"
        onClick={() => handleProjectNameClick(rowIndex, perfId, project)}
        aria-label={`View details for ${projectName ?? ''}`}
      >
        {projectName ?? ""}
      </button>
    ),
    [handleProjectNameClick],
  );

  const buildProjectRow = useCallback(
    (project: any, index: number, isFirstRow: boolean = false) => ({
      rowKey: `project-${project.id ?? project.perfId}-${isFirstRow ? 0 : index}`,
      deleteIcon: canShowDeleteActions ? getDeleteButton(project, 'Delete') : null,
      id: project.perfId,
      status: project.status === 'Closed' ? 'Complete' : (project.status ?? ""),
      lastUpdated: formatTimestampMDY(project.lastUpdated) ?? "",
      startDate: formatTimestampMDY(project.startDate) ?? "",
      nameOfProject: renderProjectNameButton(startIndex + index, project.perfId, project.nameOfProject, project),
      focusArea: project.focusArea ?? "",
      performanceMetric: project.performanceMetric ?? "",
      viewDetails: renderViewDetailsButton(startIndex + index, project.perfId, project),
    }),
    [canShowDeleteActions, startIndex, renderProjectNameButton, renderViewDetailsButton],
  );

  const projectRow: TableRow[] = useMemo(
    () => (projectList ?? []).map((project: any, index: number) => buildProjectRow(project, index, index === 0)),
    [projectList, buildProjectRow],
  );

  const renderProjectContent = () => {
    if (projectDetailsLoading) {
      return <Loader />;
    }

    if (projectDetailsError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] px-4">
          <img
            src="/assets/images/alertSolid.svg"
            alt="error-icon"
            className="w-16 h-16 mb-4"
          />
          <h3 className="h3 text-center mb-2">Something Went Wrong</h3>
        </div>
      );
    }

    const hasProjects = Array.isArray(projectList) && projectList.length > 0;
    const suppressNoProjectBannerForDelete =
      !isDeleteFlowLoading &&
      !projectListLoading &&
      !hasProjects &&
      delayNoProjectBannerUntilDeleteBannerEnds &&
      deleteBannerPlacement === 'detail';
    const showTabs = hasProjects || forceShowTabs;

    if (!showTabs) {
      if (suppressNoProjectBannerForDelete) {
        return null;
      }

      return (
        <div className="mb-4! w-full mt-10!">
          <NoProjectBanner show={true} canEdit={canEditProjects} onAdd={() => {
            setForceShowTabs(true);
            setActiveTabId(0);
            setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
          }} />
        </div>
      );
    }

    return (
      <div>
        {showNoTocDataBanner && (
          <div ref={noTocDataBannerRef} data-testid="no-toc-data-banner-wrapper">
            <CommonErrorBanner
              heading="Alert"
              messageInfo={TOC_NO_DATA_BANNER_MESSAGE}
              variant="Alert"
            />
          </div>
        )}
        {testDataChartsWarning && (
          <div data-testid="no-test-data-banner-wrapper">
            <CommonErrorBanner
              heading="Alert"
              messageInfo={testDataChartsWarning}
              variant="Alert"
            />
          </div>
        )}
        <div className="flex justify-end">
          <div className="flex items-center gap-4 mb-6 mt-4">
            <div className="flex justify-end my-4! items-end!">
              <div className="flex">
                {testDataChartsLoading && <Loader />}
                <Dropdown
                  options={getDetailsContainerProjectReportOptions()}
                  menuClassName="w-full! min-w-85!"
                  buttonClassName="w-full! min-w-50!"
                  onSelect={handleProjectReportExport}
                  disabled={testDataChartsLoading}
                  buttonLabel="Project Reports & Exports"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4!" ref={tabsRef}>
          <Tabs
            tabs={projectTabs}
            activeTabId={activeTabId}
            extraProps={{
              isAdmin,
              canEdit: canEditProjects,
              canDelete: Boolean(normalizedProjectAccess?.canDelete ?? isAdmin),
              hasProjects,
              project: projectDetails,
              selectedProject: selectedProject,
              selectedTestOfChange,
              setSelectedTestOfChange,
              testOfChangeList,
              testOfChangeCurrentPage,
              testOfChangePerPage,
              testOfChangeLoading,
              testOfChangeError,
              testOfChangeTotalItems,
              onSetTestsOfChangePage: handleSetTestsOfChangePage,
              onSetTestsOfChangeSort: handleSetTestsOfChangeSort,
              testDataSeriesItems,
              testDataSeriesCurrentPage,
              testDataSeriesPerPage,
              testDataSeriesLoading,
              testDataSeriesError,
              testDataSeriesTotalItems,
              onSetTestDataSeriesPage: handleTestDataSeriesPageChange,
              onSetTestDataSeriesSort: handleTestDataSeriesListSort,
              testDataSeriesUiSortColumn,
              testDataSeriesSortDirection,
              onAddTestData: handleAddTestData,
              onDeleteTestDataSeries: handleDeleteTestDataSeries,
              onAddTestOfChange: handleAddTestOfChange,
              onEditTestOfChange: handleEditTestOfChange,
              onDeleteTestOfChange: async (tocId: number) => {
                try {
                  const projectId =
                    selectedProject?.perfId ?? projectDetails?.perfId;
                  if (!projectId) {
                    const noProjErr = new Error(
                      "No projectId available for deleting TOC",
                    );
                    return { success: false, error: noProjErr };
                  }
                  const deleteResult: any = await dispatch(
                    deleteTestOfChange({
                      projectId,
                      tocId,
                      nuid: profile?.nuid ? String(profile.nuid) : null,
                    }),
                  ).unwrap();

                  if (
                    deleteResult?.data?.success === false ||
                    deleteResult?.success === false
                  ) {
                    return {
                      success: false,
                      error: {
                        message:
                          deleteResult?.data?.message ??
                          deleteResult?.message ??
                          "Failed to delete test of change. Please try again.",
                      },
                    };
                  }

                  // After deletion the total drops by 1. Clamp the page so we
                  // never request an empty page.
                  const newTocTotal = Math.max(0, testOfChangeTotalItems - 1);
                  const maxTocPage = Math.max(1, Math.ceil(newTocTotal / testOfChangePerPage));
                  const tocPageToFetch = Math.min(testOfChangeCurrentPage, maxTocPage);

                  if (tocPageToFetch !== testOfChangeCurrentPage) {
                    dispatch(setTestsOfChangeCurrentPage(tocPageToFetch));
                  }

                  const payload: any = {
                    projectId,
                    page: tocPageToFetch,
                    perPage: testOfChangePerPage,
                    sortColumn: testOfChangeSortColumn ?? "datelastupd",
                    countOnly: false,
                    sortOrder: testOfChangeSortDirection.toUpperCase() as
                      | "ASC"
                      | "DESC",
                  };
                  dispatch(fetchTestsOfChange(payload));
                  return { success: true };
                } catch (err) {
                  return { success: false, error: err };
                }
              },
              // pass fetched ratings/ideaSources to tabs (normalized for Dropdown)
              ratingOptions: ratingOptions,
              ideaSourceOptions: ideaSourceOptions,
              shiftOptions: shiftOptions,
              focusAreaList: focusAreaList,
              onFocusAreaChange: handleFocusAreaChange,
              metricList: metricList,
              onMetricChange: handleMetricChange,
              datasourceList: datasourceList,
              onSave: handleCreateProject,
              onUpdate: handleUpdateProject,
              showSuccessBanner: showCreateSuccessBanner,
              successBannerMessage: createSuccessMessage,
              successBannerRef,
              showFailureBanner: showManageErrorBanner,
              failureBannerMessage: manageErrorBannerMessage,
              failureBannerRef: manageErrorBannerRef,
              defaultOption: defaultShiftOption,
              triggerAddFromBanner,
              onAddFromBannerConsumed: () => {
                setTriggerAddFromBanner(false);
              },
              onAddFromBannerCancelled: () => {
                setTriggerAddFromBanner(false);
                setForceShowTabs(false);
              },
              onAddFromBannerCompleted: () => {
                setTriggerAddFromBanner(false);
              },
              // Unsaved changes tracking
              onSetUnsavedChanges: handleSetUnsavedChanges,
            }}
          />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (projectListLoading 
      || testOfChangeLoading 
      || manageProjectDependentDropdownLoading 
      || createProjectLoading
      || updateProjectLoading
      || isDeleteFlowLoading
      || teamMembersLoading
      || projectAccessLoading
      || isTeamTrackedLoading) {
      return <Loader />;
    }


    return projectRow.length > 0 ? (
      <div>
        <CardTable>
          <Table
            columns={pipListColumn}
            rows={projectRow}
            onSort={(columnKey, sortDirection) =>
              handleProjectListSort(columnKey, sortDirection.toLowerCase() as "asc" | "desc")
            }
            sortColumn={projectListUiSortColumn}
            sortDirection={projectListSortDirection.toUpperCase() as "ASC" | "DESC"}
          />
          <div className="mt-4 mobile:relative mobile:left-7.5">
            <div className="mt-4 mobile:relative mobile:left-7.5">
              <Pagination
                totalItems={projectListTotalItems}
                itemsPerPage={projectListPerPage}
                currentPage={projectListCurrentPage}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </CardTable>
      </div>
    ) : (
      null
    );

  };

    const ubtNameForBreadcrumb = (teamDetails?.teamName ?? "").trim();
  const truncatedUbtName =
    ubtNameForBreadcrumb.length > 25
      ? `${ubtNameForBreadcrumb.slice(0, 25)}...`
      : ubtNameForBreadcrumb;
  const projectsCrumbLabel = truncatedUbtName
    ? `Projects: ${truncatedUbtName}`
    : "Projects";

  const hasProjects = Array.isArray(projectList) && projectList.length > 0;
  const suppressNoProjectBannerForDelete =
    !isDeleteFlowLoading &&
    !projectListLoading &&
    !hasProjects &&
    delayNoProjectBannerUntilDeleteBannerEnds &&
    deleteBannerPlacement === 'detail';
  const showNoProjectBanner =
    !isDeleteFlowLoading &&
    !projectListLoading &&
    Array.isArray(projectList) &&
    projectList.length === 0 &&
    !forceShowTabs &&
    !suppressNoProjectBannerForDelete;

  // When there are no projects, clear stale project/details/tests-of-change/test-data state
  useEffect(() => {
    const noProjects = !projectListLoading && (!Array.isArray(projectList) || projectList.length === 0);
    if (noProjects) {
      // Clear slice state to avoid showing stale data in tabs
      dispatch(resetProjectDetails());
      dispatch(resetTestsOfChangeState());
      dispatch(resetTestDataSeriesState());
      // Clear local selections
      setSelectedProject(null);
      setSelectedTestOfChange(null);
      setActiveTabId(undefined);
      setViewingIndex(null);
    }
  }, [projectListLoading, projectList, dispatch]);

  if (!selectedTeamId) {
    return <NoTeamSelectedNotification onBreadcrumbClick={handleBreadcrumbPrevious} />;
  }

  return (
    <div className="min-h-[70vh] lg:mx-40! mx-5!">
      <div className="flex justify-between items-end">
        <div className="mb-6!">
          <BreadCrumbs
            items={[
              {
                label: 'UBT Home',
                path: '/home',
                onClick: handleBreadcrumbPrevious,
              },
              {
                label: 'Select UBT',
                path: '/home/select-ubt',
              },
              {
                label: projectsCrumbLabel,
              },
            ]}
          />
        </div>
        
        <div className="mobile-margin lg:mt-15!">
          <div className="about-this-team" style={{ color: '#677083' }}> About this team :</div>
          <Link
            to="/home/path-to-performance"
            state={{ teamId: selectedTeamId, from: 'Projects'}}
            data-analytics-type="link"
            className="no-underline! p1 -medium"
          >
            P2P
          </Link>
          <Link
            to="/home/members"
            data-analytics-type="link"
            state={{ teamId: teamDetails?.teamId ?? selectedTeamId, from: "Projects" }}
            className="no-underline! p1 -medium pl-6!"
          >
            Members
          </Link>
          <Link
            to="/home/history"
            data-analytics-type="link"
            state={{ teamId: teamDetails?.teamId ?? selectedTeamId, from: "Projects" }}
            className="no-underline! p1 -medium pl-6!"
          >
            History
          </Link>
          <Link
            to="/home/team-documents"
            data-analytics-type="link"
            state={{ teamId: teamDetails?.teamId ?? selectedTeamId, from: "Projects" }}
            className="no-underline! p1 -medium pl-6!"
          >
            Team documents
          </Link>
          {!isTeamTracked && (
            <button
              onClick={handleAddTeamToMyTeams}
              disabled={addTeamToMyTeamsLoading}
              data-analytics-type="button"
              className="no-underline! p1 -medium pl-6! bg-transparent border-none cursor-pointer hover:underline"
              style={{ color: 'rgb(0, 116, 173)' }}
              type="button"
            >
              Add to my teams
            </button>
          )}
        </div>
      </div>
      <div>
        {isDeleteFlowLoading && <Loader />}
        <h1 className="h1 tablet:mt-8! mobile:mt-3.5!">
          Performance improvement projects
        </h1>
        <h2 className="h2 tablet:mt-8! mobile:mt-3.5! w-full max-w-full whitespace-normal break-all [overflow-wrap:anywhere]">
          {ubtNameForBreadcrumb}
        </h2>
        <div className="ml-4!">
          <h3 className="p1 mt-[56px]!">
            Performance improvement projects & tests of change
          </h3>
          <ProjectFailureBanner
            show={showErrorBanner && errorBannerPlacement === "summary"}
            message={errorBannerMessage}
            bannerRef={errorBannerRef}
            placement="summary"
          />
          <DeleteBanner
            show={showDeleteBanner && deleteBannerPlacement === "summary"}
            bannerText={deletedResponseName}
          />

          {showAddTeamBanner && (
            <CommonErrorBanner
              heading={addTeamBannerVariant === "Success" ? "Success" : "Error"}
              messageInfo={addTeamBannerMessage}
              variant={addTeamBannerVariant}
            />
          )}

          <TeamDetailsCard teamRowDetail={teamRowDetail} teamsColumns={teamsColumns} />

          <div className="flex justify-end mt-12!">
            <div className="flex items-center gap-4 mb-6 mt-4">
              <div className="flex justify-end my-4! items-end!">
                <div className="flex">
                  {(tocExportLoading || reportLoading || pdfDownloadLoading || projectPdfLoading || tocPdfLoading) && <Loader />}
                  {showTocExportErrorBanner && tocExportError && (
                    <CommonErrorBanner
                      heading="Error"
                      messageInfo={tocExportError}
                      variant="Error"
                    />
                  )}
                   {hasProjects && (
                  <Dropdown
                    options={teamReportOptions}
                    menuClassName="w-full! min-w-85!"
                    buttonClassName="w-full! min-w-50!"
                    onSelect={handleReportExport}
                    disabled={tocExportLoading || reportLoading || pdfDownloadLoading || projectPdfLoading || tocPdfLoading}
                    buttonLabel="Team Reports & Exports"
                  />
                   )}
                </div>
              </div>
            </div>
          </div>
          <div>
            <DeleteBanner
              show={showDeleteBanner && deleteBannerPlacement === "detail"}
              bannerText={deletedResponseName}
            />
            {hasProjects && (
              <h3 className="p1 mt-[0px]!">
                Performance improvement projects - Detail
              </h3>
            )}
            <ProjectFailureBanner
              show={hasProjects && showErrorBanner && errorBannerPlacement === "detail"}
              message={errorBannerMessage}
              bannerRef={errorBannerRef}
              placement="detail"
            />

            {showNoProjectBanner ? (
              <div className="mb-4! w-full mt-10!">
                <NoProjectBanner show={true} canEdit={canEditProjects} onAdd={() => { setTriggerAddFromBanner(true); setForceShowTabs(true); setActiveTabId(0); setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0); }} />
              </div>
            ) : (
              <>
                {renderContent()}

                {hasProjects && (
                  <div className="flex justify-end mt-12!">
                    <div className="flex items-center gap-4 mb-6 mt-4">
                      <div className="flex justify-end my-4! items-end!" />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {!showNoProjectBanner && renderProjectContent()}
      </div>
      <div>
        <DeleteModal isOpen={showDeleteModal} title="Delete Project" deleteInfo={"Are you sure you want to delete this project?"} deleteButtonInfo="Delete Project" onCancel={handleCancelDelete} onConfirm={handleConfirmDelete} />
      </div>
      <div>
        <DeleteModal isOpen={showTeamDeleteModel} title="Delete Team" deleteInfo={"Do you want to delete this team? This action can not be undone."} deleteButtonInfo="Delete Team" onCancel={handleCancelDelete} onConfirm={handleConfirmDelete} />
      </div>

      {/* Unsaved changes modals */}
      {/* Modal for breadcrumb and browser navigation */}
      <Modal
        isOpen={showLeaveModal}
        title="Leave without saving changes?"
        clearBtn="Cancel"
        submitBtn="Leave Page"
        onSecondary={handleCancelLeave}
        onPrimary={handleLeavePage}
        onClose={handleCancelLeave}
      >
        <div className="flex items-center gap-3">
          <img
            src="/assets/images/alertSolid.svg"
            alt="alert-icon"
            className="w-12 h-12 shrink-0 mb-4!"
          />
          <p className="p3 -book mb-4!">
            You have unsaved changes. Leaving this page will discard all changes. Are you sure you want to leave?
          </p>
        </div>
        <style>
          {`
            #modal-manual-trig .modal-container {
              max-width: 50% !important;
              width: 90% !important;
              top: 20% !important;
            }
          `}
        </style>
      </Modal>

      {/* Modal for project name link navigation */}
      <Modal
        isOpen={showProjectLinkModal}
        title="Leave without saving changes?"
        clearBtn="Cancel"
        submitBtn="Leave Page"
        onSecondary={handleCancelProjectNavigation}
        onPrimary={handleConfirmProjectNavigation}
        onClose={handleCancelProjectNavigation}
      >
        <div className="flex items-center gap-3">
          <img
            src="/assets/images/alertSolid.svg"
            alt="alert-icon"
            className="w-12 h-12 shrink-0 mb-4!"
          />
          <p className="p3 -book mb-4!">
            You have unsaved changes. Switching projects will discard all changes. Are you sure you want to continue?
          </p>
        </div>
        <style>
          {`
            #modal-manual-trig .modal-container {
              max-width: 50% !important;
              width: 90% !important;
              top: 20% !important;
            }
          `}
        </style>
      </Modal>

      {/* Modal for pagination navigation */}
      <Modal
        isOpen={showPaginationModal}
        title="Leave without saving changes?"
        clearBtn="Cancel"
        submitBtn="Leave Page"
        onSecondary={handleCancelPaginationNavigation}
        onPrimary={handleConfirmPaginationNavigation}
        onClose={handleCancelPaginationNavigation}
      >
        <div className="flex items-center gap-3">
          <img
            src="/assets/images/alertSolid.svg"
            alt="alert-icon"
            className="w-12 h-12 shrink-0 mb-4!"
          />
          <p className="p3 -book mb-4!">
            You have unsaved changes. Changing pages will discard all changes. Are you sure you want to continue?
          </p>
        </div>
        <style>
          {`
            #modal-manual-trig .modal-container {
              max-width: 50% !important;
              width: 90% !important;
              top: 20% !important;
            }
          `}
        </style>
      </Modal>
    </div>
  );
};

export default ProjectDetails;